import { useEffect, useState } from "react";
import { type CustomHeaderProps } from "ag-grid-react";
import { ArrowSort_1Icon } from "vega-icons";

import { TooltipInfoIcon } from "@vega-core/components/Tooltip/TooltipInfoIcon";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";

type ApiKeysTableHeaderProps = CustomHeaderProps & {
  /** Info-icon tooltip next to the label, mirroring CoreTable's headerTooltip. */
  tooltip?: string;
};

/**
 * Custom AG Grid header that keeps the CoreTable header UX: label + neutral
 * sort glyph + info-icon tooltip. A custom header owns the sort interaction,
 * so the whole cell click progresses the sort (asc → desc → none) while the
 * info icon stays inert as a sort trigger.
 */
export const ApiKeysTableHeader = ({
  displayName,
  column,
  progressSort,
  enableSorting,
  tooltip,
}: ApiKeysTableHeaderProps) => {
  const [sort, setSort] = useState(() => column.getSort() ?? null);

  useEffect(() => {
    const onSortChanged = () => setSort(column.getSort() ?? null);
    column.addEventListener("sortChanged", onSortChanged);

    return () => column.removeEventListener("sortChanged", onSortChanged);
  }, [column]);

  const isSorted = sort === "asc" || sort === "desc";

  return (
    <div
      className={cnTwMerge(
        "flex gap-1 w-full items-center min-w-0 body-small-medium",
        isSorted ? "text-text/primary" : "text-text/secondary",
        enableSorting && "cursor-pointer select-none",
      )}
      data-testid={`header-${column.getColId()}`}
      onClick={
        enableSorting ? (event) => progressSort(event.shiftKey) : undefined
      }
    >
      <span className="truncate min-w-0">{displayName}</span>
      {enableSorting ? (
        <ArrowSort_1Icon className="w-4 h-4 defaultTransition" />
      ) : null}
      {tooltip ? (
        /* Interacting with the info icon must not double as a sort trigger. */
        <span onClick={(event) => event.stopPropagation()}>
          <TooltipInfoIcon
            className={cnTwMerge(
              isSorted ? "text-text/primary" : "text-text/secondary",
            )}
            content={tooltip}
            tooltipContentClassname="text-text/secondary"
          />
        </span>
      ) : null}
    </div>
  );
};
